import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useDeepCompareEffectNoCheck } from "use-deep-compare-effect";
import { dispatch, getState } from "../../state/store";
import {
  resetFWUCampaigns,
  setFwuCampaingsError,
  setFwuCampaingsLoading,
  setFwuCampaingsRefresh,
  setFWUCampaignToEdit
} from "../../state/ducks";
import { selectFwuCampaigns, selectFwuCampaignToEdit } from "../../state/selectors";

import { strCode } from "../../consts";
import { fwuCampaignsService, log, notificationService, t } from "../../services";

export const useFetchFWUCampaigns = () => {
  const fwuCampaigns = useSelector((store) => store.fwuCampaigns?.fwuCampaigns);
  const loading = useSelector((store) => store.fwuCampaigns?.loading);
  const refresh = useSelector((store) => store.fwuCampaigns?.refresh);

  const filters = useSelector((store) => store.fleetManagement.fwuCampaignFilters);
  const { markets } = filters;

  useDeepCompareEffectNoCheck(() => {
    dispatch(setFwuCampaingsRefresh(true));
  }, [markets]);

  const refreshFWUCampaignToEdit = () => {
    const fwuCampaigns = selectFwuCampaigns()(getState());
    const fwuCampaignToEdit = selectFwuCampaignToEdit()(getState());

    if (fwuCampaignToEdit && fwuCampaigns?.length) {
      const campaignName = fwuCampaignToEdit.campaignName;

      const refreshedFWUCampaignToEdit = fwuCampaigns.find(
        (campaign) => campaign.campaignName === campaignName
      );

      if (refreshedFWUCampaignToEdit) {
        dispatch(setFWUCampaignToEdit(refreshedFWUCampaignToEdit));
      }
    }
  };

  const fetchCallback = useCallback(
    async (isRefresh) => {
      if ((fwuCampaigns !== null && !isRefresh) || loading) {
        return;
      }

      dispatch(setFwuCampaingsError(null));
      dispatch(setFwuCampaingsLoading(true));

      try {
        dispatch(resetFWUCampaigns());

        await fwuCampaignsService.fetchFWUCampaigns(markets);

        if (isRefresh) {
          refreshFWUCampaignToEdit();
        }
      } catch (err) {
        log.error("useFetchFWUCampaigns: Failed to fetch FWU campaigns", err);

        dispatch(setFwuCampaingsError(err));

        notificationService.showError({
          title: t(strCode.FETCH_FWU_CAMPAIGNS_ERROR),
          message: err?.response?.data ? err.response.data : err?.toString()
        });
      } finally {
        dispatch(setFwuCampaingsLoading(false));
        dispatch(setFwuCampaingsRefresh(false));
      }
    },
    [refresh]
  );

  return { fetchCallback, data: fwuCampaigns, loading, refresh };
};
